import React from 'react';
import clsx from 'clsx';
import type {SetRequired} from 'type-fest';
import {faCaretRight, faCheck} from '@fortawesome/pro-solid-svg-icons';
import {useHash} from '@netx/core-components/hooks/useHash';
import {MenuItem, type MenuItemProps} from '@netx/core-components/MenuItem';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {isRouteMatch} from '@netx/core-next/utils/route';
import {type ReactAction} from '@netx/core/bootstrap';
import {
	ACTION_MENU_LIST_ITEM_CLASS_NAME,
	actionMenuListItemClasses,
	ActionMenuListItemClasses,
	getActionMenuListItemUtilityClass,
} from './actionMenuListItemClasses';

type ActionMenuListItemOwnerState = WithClasses<
	ActionMenuListItemClasses,
	{
		showSelectionIcon?: boolean;
	}
>;

const useUtilityClasses = (state: ActionMenuListItemOwnerState) => {
	const {classes} = state;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getActionMenuListItemUtilityClass, classes);
};

const ActionMenuListItemRoot = styled(MenuItem, {
	name: ACTION_MENU_LIST_ITEM_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => ({
	...(ownerState.showSelectionIcon && {
		'& .MuiListItemText-root': {
			marginLeft: 40,
		},
	}),
}));

export interface ActionMenuListItemProps
	extends
		Omit<
			SetRequired<MenuItemProps, 'onClick'>,
			'classes' | 'sx' | 'action' | 'text' | 'selected'
		>,
		WithClasses<ActionMenuListItemClasses> {
	disableIcon?: boolean;
	action: ReactAction;
	someSelected?: boolean;
	selected?: boolean | ((action: ReactAction) => boolean);
}

export const ActionMenuListItem = React.forwardRef<
	HTMLLIElement,
	ActionMenuListItemProps
>(
	(
		{
			action,
			className,
			classes: _classes,
			disableIcon,
			someSelected,
			onClick,
			...props
		},
		ref,
	) => {
		const hash = useHash();

		const showSelectionIcon =
			(disableIcon === true || action.icon == null) && someSelected;

		const ownerState: ActionMenuListItemOwnerState = {
			classes: _classes,
			className,
			showSelectionIcon: showSelectionIcon && !action.selected,
		};

		const classes = useUtilityClasses(ownerState);

		const routeMatch = isRouteMatch(hash.currentRoute, action.routeMatch);

		return (
			<ActionMenuListItemRoot
				ref={ref}
				className={clsx(classes.root, className)}
				disableRipple={!!action.children?.length}
				depth={action.depth}
				proxy={action.proxy}
				endIcon={action.children?.length ? faCaretRight : undefined}
				icon={
					showSelectionIcon && action.selected
						? faCheck
						: disableIcon === true
							? undefined
							: action.icon
				}
				severity={action.severity}
				text={action.label}
				ownerState={ownerState}
				{...props}
				selected={
					typeof props.selected === 'function'
						? props.selected(action)
						: props.selected || routeMatch || action.selected
				}
				onClick={(event: React.MouseEvent<HTMLLIElement>) => {
					event.stopPropagation();
					event.preventDefault();
					if (action.children?.length || action.proxy) return;
					onClick(event);
				}}
			/>
		);
	},
);

export default ActionMenuListItem;
